import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Carte from './pages/Carte';
import AntennesList from './pages/AntennesList';
import AntenneDetail from './pages/AntenneDetail';
import AntenneForm from './pages/AntenneForm';
import ContactsList from './pages/ContactsList';
import ContactDetail from './pages/ContactDetail';
import ContactForm from './pages/ContactForm';
import BinomeForm from './pages/BinomeForm';
// import Admin from './pages/Admin'; // DISABLED — admin panel centralise
import Parametres from './pages/Parametres';
// import AdminDbManager from './pages/AdminDbManager'; // DISABLED — admin panel centralise
import Tarifs from './pages/Tarifs';
import ApportsAffaire from './pages/ApportsAffaire';
import PretsBornes from './pages/PretsBornes';
import AccessDenied from './pages/AccessDenied';
import Loading from './components/Loading';
import { PageGuard } from './lib/perms';

import './App.css';

/**
 * Page demandee AVANT la connexion, a rejoindre une fois connecte.
 *
 * Keycloak nous ramene toujours sur `redirect_uri`, qui vaut la racine du site
 * (index.js) — et doit le rester, sinon il faudrait declarer chaque URL de
 * l'app dans le client Keycloak. Consequence : sans memorisation, ouvrir
 * directement `/contacts/284` renvoyait sur le tableau de bord.
 *
 * ⚠️ Le magasin d'oidc-client-ts est `sessionStorage`, donc PROPRE A L'ONGLET :
 * un lien colle dans un NOUVEL onglet n'est jamais authentifie, meme si la
 * session Keycloak est ouverte a cote. C'est le cas courant, pas le cas rare
 * — et c'est aussi celui de nos propres liens `target="_blank"`.
 */
const CLE_RETOUR = 'antennes:retour-apres-connexion';

function memoriserRetour(chemin) {
  // La racine n'a rien a restaurer : ce serait la destination par defaut.
  if (!chemin || chemin === '/') return;
  try {
    sessionStorage.setItem(CLE_RETOUR, chemin);
  } catch {
    // Navigation privee ou stockage plein : on perd la restauration, pas la
    // connexion. Rien de plus a faire.
  }
}

function reprendreRetour() {
  let chemin = null;
  try {
    chemin = sessionStorage.getItem(CLE_RETOUR);
    // Retire AVANT toute navigation : une valeur qui survit rejouerait la
    // redirection a chaque passage.
    sessionStorage.removeItem(CLE_RETOUR);
  } catch {
    return null;
  }
  // Un chemin interne, et rien d'autre : `//evil.com` est une URL absolue pour
  // le navigateur, ce serait une redirection ouverte.
  if (!chemin || !chemin.startsWith('/') || chemin.startsWith('//')) return null;
  return chemin;
}

function ProtectedRoute({ children }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Silent token renewal is handled automatically by oidc-context via
  // `automaticSilentRenew: true` in oidcConfig (index.js). No manual
  // signinSilent needed here — it was causing visible full-page reloads
  // when the silent renew failed (removeUser → signinRedirect loop).

  // Retour de Keycloak : on rejoint la page demandee au depart.
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const retour = reprendreRetour();
    if (retour && retour !== location.pathname + location.search) {
      navigate(retour, { replace: true });
    }
    // Volontairement sur le seul etat d'authentification : `location` change a
    // chaque navigation, et relancer ceci ne servirait a rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  if (auth.isLoading) {
    return <Loading />;
  }

  if (auth.error) {
    return (
      <div className="error-page">
        <h2>Erreur d'authentification</h2>
        <p>{auth.error.message}</p>
        <button
          onClick={() => {
            memoriserRetour(location.pathname + location.search + location.hash);
            auth.signinRedirect();
          }}
        >
          Se reconnecter
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    // Ecrit ici, et pas dans un effet : `signinRedirect()` part des ce rendu,
    // un effet arriverait apres coup.
    memoriserRetour(location.pathname + location.search + location.hash);
    auth.signinRedirect();
    return <Loading message="Redirection vers la page de connexion..." />;
  }

  return children;
}

function AdminRoute({ children }) {
  const auth = useAuth();

  // Roles can be in profile (id_token) or access_token
  let roles = auth.user?.profile?.realm_access?.roles || [];
  if (!roles.length && auth.user?.access_token) {
    try {
      const payload = JSON.parse(atob(auth.user.access_token.split('.')[1]));
      roles = payload.realm_access?.roles || [];
    } catch {}
  }
  const isAdmin = roles.includes('admin');

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <Router>
      <div className="app">
        <Routes>

          {/* Public 403 page (no auth required for the render itself) */}
          <Route path="/403" element={<AccessDenied />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PageGuard perm="pages.dashboard.view"><Dashboard /></PageGuard>} />
            <Route path="carte" element={<PageGuard perm="pages.carte.view"><Carte /></PageGuard>} />
            <Route path="antennes" element={<PageGuard perm="pages.antennes.view"><AntennesList /></PageGuard>} />
            <Route path="antennes/new" element={<PageGuard perm="antennes.create"><AntenneForm /></PageGuard>} />
            <Route path="antennes/:id" element={<PageGuard perm="pages.antenne_detail.view"><AntenneDetail /></PageGuard>} />
            <Route path="antennes/:id/edit" element={<PageGuard perm="antennes.edit"><AntenneForm /></PageGuard>} />
            <Route path="contacts" element={<PageGuard perm="pages.contacts.view"><ContactsList /></PageGuard>} />
            <Route path="contacts/new" element={<PageGuard perm="contacts.create"><ContactForm /></PageGuard>} />
            <Route path="contacts/:id" element={<PageGuard perm="pages.contact_detail.view"><ContactDetail /></PageGuard>} />
            <Route path="contacts/:id/edit" element={<PageGuard perm="contacts.edit"><ContactForm /></PageGuard>} />
            <Route path="contacts/:id/binomes/new" element={<PageGuard perm="binomes.create"><BinomeForm /></PageGuard>} />
            <Route path="tarifs" element={<PageGuard perm="pages.tarifs.view"><Tarifs /></PageGuard>} />
            <Route path="tarif_beta" element={<Navigate to="/tarifs" replace />} />
            <Route path="apports-affaire" element={<PageGuard perm="pages.apports_affaire.view"><ApportsAffaire /></PageGuard>} />
            <Route path="prets-bornes" element={<PageGuard perm="pages.prets_bornes.view"><PretsBornes /></PageGuard>} />
            {/* <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} /> */}{/* DISABLED — admin panel centralise */}
            <Route path="parametres" element={<AdminRoute><PageGuard perm="pages.parametres.view"><Parametres /></PageGuard></AdminRoute>} />
            {/* <Route path="dbmanager" element={<AdminRoute><AdminDbManager /></AdminRoute>} /> */}{/* DISABLED — admin panel centralise */}
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

export default App;
